import { useState, useContext, useEffect } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import IconTextField from "../components/IconTextField";
import { EmailOutlined } from "@mui/icons-material";
import loginbg from "../assets/login-image.png";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import appTheme from "../themes/theme";
import { AuthContext } from "../AuthContext";
import { checkDealerOnboard, verifyDealer } from "../services/dealerServices";
import { getUserDetails, updateUserTrems } from "../services/userservice";
import { IconButton, InputAdornment, CircularProgress } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { encryptData, decryptData } from "../utils/encryption";
import isLocalAuthBypass from "../utils/localAuthBypass";
import { postSignInPath, returnPath } from "../utils/deepLink";
import TermsOfUsePanel from "../components/TermsOfUsePanel";

const SignIn = () => {
  const {
    user,
    requiredNewPwd,
    newPasswordAttr,
    signIn,
    signOut,
    group,
    setDealer,
    setIsOnboarded,
    setOnboarding,
    loginError,
    loginUser,
    setLoginUser,
  } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  // Set by RouteGuard when it sent a signed-out user here; honoured once they are signed in.
  const from = useLocation().state?.from;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [passwordErr, setPasswordErr] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(false);
  const [isLoginTriggered, setIsLoginTriggered] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const [navError, setNavError] = useState("");
  const [redirectTimer, setRedirectTimer] = useState(null);
  const [loginBlocked, setLoginBlocked] = useState(false);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  // LOCAL DEV ONLY (dev-local/): "/" renders SignIn unconditionally, and the normal
  // post-login navigation below is gated on isLoginTriggered, which only becomes true after
  // a real form submit. Send the bypassed session where a real admin login lands.
  useEffect(() => {
    if (process.env.NODE_ENV === "development" && isLocalAuthBypass() && user) {
      navigate(returnPath(from, "/home/overview"), { replace: true });
    }
  }, [user, navigate, from]);

  useEffect(() => {
    if (id) {
      const secode = id.split('&code=')[1];
      const decoded = decodeURIComponent(id.split('&code=')[0]);
      const dyData = decryptData(decoded);

      console.log("dycrepeted Data :", dyData);
      if (dyData && dyData.email) {
        setEmail(dyData.email);
        setPassword(secode);
      }
    }
  }, [id]);

  useEffect(() => {
    if (!isFormSubmitted || loginBlocked) return;

    // trap current page in history
    window.history.pushState(null, "", window.location.href);

    const handleBack = () => {
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, [isFormSubmitted, loginBlocked]);

  const signupNavigation = () => {
    navigate("signup");
  };

  const handleLoginFailure = (message = "Currently unable to login. Please try again later.") => {
    setLoginErr(message);
    setIsNavigating(false);
    setPendingNavigation(false);
    setIsFormSubmitted(false);
    setLoginBlocked(true);
    setIsLoginTriggered(true);
    if (redirectTimer) clearTimeout(redirectTimer);

    const timer = setTimeout(() => {
      signOut();
      navigate("/");
    }, 5000);

    setRedirectTimer(timer);
  };

  const forgotPwdNavigation = () => {
    navigate("forgotpassword");
  };

  const formInputChange = (formField, value) => {
    if (formField === "email") {
      setEmail(value);
      setEmailErr("");
    }
    if (formField === "password") {
      setPassword(value);
      setPasswordErr("");
    }
  };

  const validation = () => {
    let isValid = true;

    if (!email) {
      setEmailErr("Email is Required");
      isValid = false;
    }

    if (!password) {
      setPasswordErr("Password is required");
      isValid = false;
    } else if (password.length < 6) {
      setPasswordErr("Must be at least 6 characters");
      isValid = false;
    }

    return isValid;
  };

  const handleClick = async (event) => {
    event.preventDefault();
    setIsLoginTriggered(true);
    setEmailErr("");
    setPasswordErr("");
    setLoginErr("");

    if (!validation()) {
      return;
    }

    setIsFormSubmitted(true);

    try {
      await signIn(email, password);
    } catch (err) {
      console.error("Login failed:", err);
      setIsFormSubmitted(false);
    }
  };

  useEffect(() => {
    if (loginError) {
      setLoginErr(loginError);
      setIsFormSubmitted(false);
    }
  }, [loginError]);

  const handleUserNavigation = async () => {
    console.log("Logged in user details ---> ", user);
    if (loginBlocked) return;
    setIsNavigating(true);

    try {
      if (group !== "jbw-dealer") {
        navigate(postSignInPath({ group, from }).path);
        return;
      }

      const onboardRes = await checkDealerOnboard(user.profile);
      const dealerData = onboardRes.data;
      const isError =
        dealerData &&
        typeof dealerData === "object" &&
        !Array.isArray(dealerData) &&
        dealerData.statusCode >= 400;

      if (isError) {
        console.error("API error:", dealerData);
        handleLoginFailure("Unable to verify dealer onboarding at the moment.");
        return;
      }
      if (!dealerData || dealerData.length === 0) {
        console.error("No dealer data found");
        const verifyRes = await verifyDealer(user.profile);
        const verifiedDealer = verifyRes.data;

        if (!verifiedDealer || verifiedDealer.length === 0) {
          console.error("Dealer verification failed");
          setIsNavigating(false);
          setLoginErr("Dealer verification failed ");

          handleLoginFailure("Unable to verify dealer onboarding at the moment.");
          return;
        }
        const dealer = verifiedDealer[0];
        setDealer(dealer);
        localStorage.setItem("dealer", JSON.stringify(dealer));
        setOnboarding({
          dealerId: dealer.id,
          dealerName: dealer.dealerName,
        });
        setIsOnboarded(false);
        navigate("/home/onboarding/clientlocation", {
          state: { key: "onboardinghome" },
        });
      } else {
        // Which registration to sign in as, and where to land -- see postSignInPath. A user
        // registered to several dealers who followed a link signs in as the linked dealer.
        const next = postSignInPath({ group, from, registrations: dealerData });
        const verifyRes = await verifyDealer(next.dealerId);
        const verifiedDealer = verifyRes.data;

        if (!verifiedDealer || verifiedDealer.length === 0) {
          console.error("Dealer verification failed");
          setIsNavigating(false);
          setLoginErr("Dealer verification failed ");
          return;
        }

        const dealer = verifiedDealer[0];
        setDealer(dealer);
        localStorage.setItem("dealer", JSON.stringify(dealer));
        navigate(next.path);
      }
    } catch (error) {
      console.error("Navigation error:", error);
      setIsNavigating(false);
    }
  };

  const handleTermsAccept = async () => {
    setShowTermsDialog(false);
    try {
      await updateUserTrems(loginUser.id, 1);
      setLoginUser({ ...loginUser, isTermsAccepted: 1 });
    } catch (err) {
      console.error("Failed to update terms acceptance:", err);
    }
    handleUserNavigation();
  };

  const handleTermsDecline = () => {
    setShowTermsDialog(false);
    setPendingNavigation(false);
    setIsFormSubmitted(false);
    setIsNavigating(false);
    signOut();
  };

  useEffect(() => {
    if (loginBlocked) return;
    if (requiredNewPwd) {
      console.log("Password change required");
      console.log("newPasswordattr :", newPasswordAttr);
      const encryptedData = encodeURIComponent(encryptData(newPasswordAttr));
      navigate(`/changepassword/${encryptedData}`);
    }
    if (!isLoginTriggered) return;
    if (!user || isNavigating || pendingNavigation) return;

    const checkTermsAndNavigate = async () => {
      setPendingNavigation(true);
      try {
        const res = await getUserDetails(user.email);
        const userDetails = res.data;
        setLoginUser(userDetails);
        if (userDetails?.isTermsAccepted === 1) {
          // Terms already accepted - go straight to app
          await handleUserNavigation();
        } else {
          // Terms not yet accepted - show terms panel
          setShowTermsDialog(true);
        }
      } catch (err) {
        console.error("Failed to fetch user details:", err);
        setLoginErr("Failed to load user details. Please try again.");
        setPendingNavigation(false);
        setIsFormSubmitted(false);
      }
    };

    checkTermsAndNavigate();
  }, [
    user,
    requiredNewPwd,
    isNavigating,
    pendingNavigation,
  ]);

  return (
    <ThemeProvider theme={appTheme}>
      <Grid container sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: `url(${loginbg})`,
            backgroundRepeat: "no-repeat",
            backgroundColor: (t) =>
              t.palette.mode === "light"
                ? t.palette.grey[50]
                : t.palette.grey[900],
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          {showTermsDialog ? (
            // Terms view - shown after successful login, before navigation
            <TermsOfUsePanel
              onAccept={handleTermsAccept}
              onDecline={handleTermsDecline}
            />
          ) : (
            // Login form view
            <Box
              m="auto"
              sx={{
                width: 300,
                my: 8,
                mx: 4,
                ml: 20,
                mt: 20,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography component="h1" variant="h4">
                Hello Again!
              </Typography>
              <Typography component="h1" variant="h6">
                Welcome Back
              </Typography>
              <Box
                component="form"
                noValidate
                onSubmit={handleClick}
                sx={{ mt: 1 }}
              >
                <IconTextField
                  iconStart={
                    <EmailOutlined sx={{ color: "#bdbdbd", fontSize: 20 }} />
                  }
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => formInputChange("email", e.target.value)}
                  error={!!emailErr}
                  helperText={emailErr}
                  size="small"
                  disabled={isFormSubmitted}
                />
                <IconTextField
                  iconStart={
                    <LockOutlinedIcon sx={{ color: "#bdbdbd", fontSize: 20 }} />
                  }
                  iconEnd={
                    <IconButton
                      aria-label={
                        showPassword
                          ? "hide the password"
                          : "display the password"
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      onMouseUp={handleMouseUpPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  }
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => formInputChange("password", e.target.value)}
                  error={!!passwordErr}
                  helperText={passwordErr}
                  icon={<LockOutlinedIcon />}
                  size="small"
                  disabled={isFormSubmitted}
                />
                {loginErr && !redirectTimer && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {loginErr}
                  </Typography>
                )}
                {loginErr && redirectTimer && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {loginErr}
                    {redirectTimer && " Redirecting to home in 5 seconds..."}
                  </Typography>
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, borderRadius: "50px" }}
                  size="small"
                  disabled={isFormSubmitted}
                >
                  {isFormSubmitted ? <CircularProgress size={24} /> : "Login"}
                </Button>
                <Grid container>
                  <Grid item xs>
                    <Button
                      variant="text"
                      disabled={isFormSubmitted}
                      onClick={forgotPwdNavigation}
                      sx={{ cursor: "pointer", color: "primary.main" }}
                    >
                      Forgot password?
                    </Button>
                  </Grid>
                  <Grid item>
                    {/* <Button
                      variant="text"
                      disabled={isFormSubmitted}
                      onClick={signupNavigation}
                      sx={{ cursor: "pointer", color: "primary.main" }}
                    >
                     Sign Up
                    </Button> */}
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default SignIn;
